import type { ComponentProps, KeyboardEvent, RefObject } from 'react';

import { cn } from '@repo/lib/cn';
import { XIcon } from 'lucide-react';
import { createContext, use, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

import { Button, ButtonGroup } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

import type { TimeInputItemProps, TimeInputProps, TimePart, TimePickerContextProps } from '.';

import { TimeInputManager } from '.';

const TimePickerContext = createContext<TimePickerContextProps | null>(null);

export function useTimePicker() {
  const ctx = use(TimePickerContext);
  if (!ctx) throw new Error('useTimePicker must be used within a TimeInput');
  return ctx;
}

export function TimeInput(timePickerProps: TimeInputProps) {
  const { value = '', onChange, children, disabled = false, inputProps, 'aria-invalid': ariaInvalidFromSlot, ...props } = timePickerProps;

  const handleRightFocus = (ref: RefObject<HTMLElement | null>) => {
    const nextElement = ref.current?.nextElementSibling;
    if (nextElement && nextElement instanceof HTMLElement) {
      nextElement.focus();
    }
  };

  const handleLeftFocus = (ref: RefObject<HTMLElement | null>) => {
    const prevElement = ref.current?.previousElementSibling;
    if (prevElement && prevElement instanceof HTMLElement) {
      prevElement.focus();
    }
  };

  const { ref: _inputRef, 'aria-invalid': ariaInvalidFromInputProps, ...restInputProps } = inputProps || {};

  const ariaInvalid = ariaInvalidFromSlot === true || ariaInvalidFromSlot === 'true' || ariaInvalidFromInputProps === true || ariaInvalidFromInputProps === 'true';

  return (
    <TimePickerContext
      value={{
        disabled,
        value,
        onChange,
        handleRightFocus,
        handleLeftFocus,
        ariaInvalid,
      }}
    >
      <input {...restInputProps} value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)} className={'hidden'} tabIndex={-1} />
      <ButtonGroup {...props}>{children}</ButtonGroup>
    </TimePickerContext>
  );
}

export function TimeInputItem(timePickerInputProps: TimeInputItemProps) {
  const { ref, className, type = 'text', id, name, disabled, onKeyDown, picker, period, placeholder = '--', 'aria-invalid': ariaInvalidProp, ...props } = timePickerInputProps;

  const inputRef = useRef<HTMLInputElement>(null);
  useImperativeHandle(ref, () => inputRef.current!, []);

  const timePickerType: TimePart = picker === '12hours' ? 'hours' : picker;

  const { disabled: _disabled, value, onChange, handleRightFocus: _handleRightFocus, handleLeftFocus: _handleLeftFocus, ariaInvalid: _ariaInvalid } = useTimePicker();
  const handleRightFocus = () => _handleRightFocus(inputRef);
  const handleLeftFocus = () => _handleLeftFocus(inputRef);

  useImperativeHandle(ref, () => inputRef.current!, []);

  const manager = useMemo<TimeInputManager>(() => {
    const containerRef = inputRef.current?.parentElement;
    const nodes = containerRef?.querySelectorAll<HTMLInputElement>('[data-time-part-name]');
    const partsSet = new Set<TimePart>();
    nodes?.forEach((n) => {
      const p = n.getAttribute('data-time-part-name') as TimePart | null;
      if (p) partsSet.add(p);
    });
    return new TimeInputManager(partsSet);
  }, []);

  const [flag, setFlag] = useState<boolean>(false);
  const [prevIntKey, setPrevIntKey] = useState<string>('0');

  useEffect(() => {
    if (flag) {
      const timer = setTimeout(() => {
        setFlag(false);
      }, 2500);
      return () => clearTimeout(timer);
    }
  }, [flag]);

  // Get the current value for this picker part
  const calculatedValue: string = useMemo<string>(() => {
    return manager.getTimePart(value, timePickerType);
  }, [value, timePickerType, manager]);

  const calculateNewValue = (key: string): string => {
    if (picker === '12hours') {
      if (flag && calculatedValue.slice(1, 2) === '1' && prevIntKey === '0') return '0' + key;
    }
    return !flag ? '0' + key : calculatedValue.slice(1, 2) + key;
  };

  const processValue = (inputValue: string): void => {
    const digitsOnly = inputValue.replace(/\D/g, '');
    const currentValue = value || '';

    if (digitsOnly.length === 0) {
      const updated = manager.setTimePart(currentValue, timePickerType, '');
      onChange(updated);
      return;
    }

    const lastTwoDigits = digitsOnly.slice(-2);

    let validatedValue: string = lastTwoDigits;
    if (picker === 'hours') {
      validatedValue = manager.getValidHour(lastTwoDigits);
    } else if (picker === 'minutes') {
      validatedValue = manager.getValidMinuteOrSecond(lastTwoDigits);
    } else if (picker === 'seconds') {
      validatedValue = manager.getValidMinuteOrSecond(lastTwoDigits);
    } else if (picker === '12hours') {
      validatedValue = manager.getValid12Hour(lastTwoDigits);
    }

    let updated: string = currentValue;
    if (picker === 'hours') {
      updated = manager.setTimePart(currentValue, 'hours', validatedValue);
    } else if (picker === 'minutes') {
      updated = manager.setTimePart(currentValue, 'minutes', validatedValue);
    } else if (picker === 'seconds') {
      updated = manager.setTimePart(currentValue, 'seconds', validatedValue);
    } else if (picker === '12hours' && period) {
      const h24: number = manager.convert12HourTo24Hour(parseInt(validatedValue, 10), period);
      updated = manager.setTimePart(currentValue, 'hours', h24.toString().padStart(2, '0'));
    }

    onChange(updated);

    if (validatedValue.length === 2) {
      handleRightFocus();
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Tab') return;
    if (e.key === 'Backspace' || e.key === 'Delete') {
      e.preventDefault();
      const currentValue = value || '';
      const updated = manager.setTimePart(currentValue, timePickerType, '');
      onChange(updated);
      return;
    }
    e.preventDefault();
    if (e.key === 'ArrowRight') {
      handleRightFocus();
    }
    if (e.key === 'ArrowLeft') {
      handleLeftFocus();
    }
    if (['ArrowUp', 'ArrowDown'].includes(e.key)) {
      const step: number = e.key === 'ArrowUp' ? 1 : -1;
      let newValue: string = calculatedValue || '00';
      if (picker === 'hours') {
        newValue = manager.getValidArrowHour(newValue, step);
      }
      if (picker === 'minutes') {
        newValue = manager.getValidArrowMinuteOrSecond(newValue, step);
      }
      if (picker === 'seconds') {
        newValue = manager.getValidArrowMinuteOrSecond(newValue, step);
      }
      if (picker === '12hours') {
        newValue = manager.getValidArrow12Hour(newValue, step);
      }

      if (flag) setFlag(false);

      const currentValue = value || '';
      let updated: string = currentValue;
      if (picker === 'hours') updated = manager.setTimePart(currentValue, 'hours', newValue);
      if (picker === 'minutes') updated = manager.setTimePart(currentValue, 'minutes', newValue);
      if (picker === 'seconds') updated = manager.setTimePart(currentValue, 'seconds', newValue);
      if (picker === '12hours' && period) {
        const h24: number = manager.convert12HourTo24Hour(parseInt(newValue, 10), period);
        updated = manager.setTimePart(currentValue, 'hours', h24.toString().padStart(2, '0'));
      }
      onChange(updated);
    }
    if (e.key >= '0' && e.key <= '9') {
      if (picker === '12hours') setPrevIntKey(e.key);

      const newValue: string = calculateNewValue(e.key);

      let validatedValue: string = newValue;
      if (picker === 'hours') {
        validatedValue = manager.getValidHour(newValue);
      }
      if (picker === 'minutes') {
        validatedValue = manager.getValidMinuteOrSecond(newValue);
      }
      if (picker === 'seconds') {
        validatedValue = manager.getValidMinuteOrSecond(newValue);
      }
      if (picker === '12hours') {
        validatedValue = manager.getValid12Hour(newValue);
      }

      // Determine if we should auto-advance to next field
      const shouldAutoAdvance =
        flag &&
        // For hours: auto-advance if first digit is >= 3 (can't type "3X" for valid hour)
        ((picker === 'hours' && parseInt(calculatedValue.slice(0, 1), 10) >= 3) ||
          // For 12hours: auto-advance if first digit is >= 2 (can't type "2X" except "20", "21", "22", "23" which don't exist in 12h)
          (picker === '12hours' && parseInt(calculatedValue.slice(0, 1), 10) >= 2) ||
          // For minutes/seconds: auto-advance if first digit is >= 6 (can't type "6X" for valid minute/second)
          ((picker === 'minutes' || picker === 'seconds') && parseInt(calculatedValue.slice(0, 1), 10) >= 6));

      if (shouldAutoAdvance) {
        handleRightFocus();
      }

      setFlag((prev) => !prev);

      const currentValue = value || '';
      let updated: string = currentValue;
      if (picker === 'hours') updated = manager.setTimePart(currentValue, 'hours', validatedValue);
      if (picker === 'minutes') updated = manager.setTimePart(currentValue, 'minutes', validatedValue);
      if (picker === 'seconds') updated = manager.setTimePart(currentValue, 'seconds', validatedValue);
      if (picker === '12hours' && period) {
        const h24: number = manager.convert12HourTo24Hour(parseInt(validatedValue, 10), period);
        updated = manager.setTimePart(currentValue, 'hours', h24.toString().padStart(2, '0'));
      }

      onChange(updated);
    }
  };

  const ariaInvalid = ariaInvalidProp !== undefined ? ariaInvalidProp === true || ariaInvalidProp === 'true' : _ariaInvalid;

  return (
    <Input
      ref={inputRef}
      id={id || picker}
      name={name || picker}
      data-time-part-name={timePickerType}
      disabled={disabled ?? _disabled}
      aria-invalid={ariaInvalid}
      className={cn('text-center', className)}
      value={calculatedValue}
      type={type}
      inputMode={'decimal'}
      onKeyDown={(e) => {
        onKeyDown?.(e);
        handleKeyDown(e);
      }}
      onChange={(e) => {
        const inputValue = e.target.value;
        if (inputValue !== calculatedValue) {
          processValue(inputValue);
        }
      }}
      placeholder={placeholder}
      {...props}
    />
  );
}

export function TimeInputClearButton(timeInputClearButtonProps: ComponentProps<typeof Button>) {
  const { ref, disabled = false, className, variant = 'outline', onClick, ...props } = timeInputClearButtonProps;

  const buttonRef = useRef<HTMLButtonElement>(null);
  const { disabled: _disabled, onChange: _onChange, handleRightFocus, handleLeftFocus } = useTimePicker();

  useImperativeHandle(ref, () => buttonRef.current!, []);

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === 'Backspace' || e.key === 'Delete') {
      e.preventDefault();
      buttonRef.current?.click();
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      handleLeftFocus(buttonRef);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      handleRightFocus(buttonRef);
    }
  };

  return (
    <Button
      type="button"
      ref={buttonRef}
      disabled={_disabled ?? disabled}
      onKeyDown={handleKeyDown}
      variant={variant}
      onClick={(e) => {
        onClick?.(e);
        _onChange('');
      }}
      className={className}
      {...props}
    >
      <XIcon />
    </Button>
  );
}
